import { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatFullDate } from "@/lib/format";
import type { ApprovalProgress } from "@/pages/apply/hooks/useApprovalProgress";

interface ApprovalProgressStripProps {
  progress: ApprovalProgress | null;
}

// 客户侧进度条：当申请进入多级审批流后，展示当前处在第几级、逐级状态与当前节点，
// 并可点击任一节点展开该级的决策记录（审批人 + 意见 + 时间）。
export default function ApprovalProgressStrip({ progress }: ApprovalProgressStripProps) {
  const { t } = useTranslation();
  const [expandedOrder, setExpandedOrder] = useState<number | null>(null);
  if (!progress || progress.steps.length === 0) return null;

  const isPending = progress.status === "pending";
  const isApproved = progress.status === "approved";
  const isRejected = progress.status === "rejected";

  const stateClass = (status: string) => {
    if (status === "rejected") return "bg-accent-100 text-accent-800";
    if (status === "done") return "bg-primary-100 text-primary-700";
    if (status === "current") return "bg-secondary-500 text-background-50";
    return "border border-background-300 bg-background-50 text-foreground-500";
  };

  const stateIcon = (status: string) => {
    if (status === "rejected") return "ri-close-line";
    if (status === "done") return "ri-check-line";
    if (status === "current") return "ri-more-line";
    return "ri-time-line";
  };

  const expanded = progress.steps.find((step) => step.order === expandedOrder) ?? null;

  return (
    <div className="mt-3 rounded-md border border-secondary-200 bg-secondary-50 px-3.5 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-xs font-medium text-secondary-900">
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-flow-chart" />
          </span>
          {t("apply.approvalTitle")}
        </span>
        <span className="inline-flex items-center gap-1 rounded-full bg-secondary-500 px-2.5 py-0.5 text-[11px] font-medium text-background-50">
          {isApproved
            ? t("approvals.statusApproved")
            : isRejected
              ? t("approvals.statusRejected")
              : t("apply.approvalLevel", { current: progress.currentStep, total: progress.totalSteps })}
        </span>
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        {progress.steps.map((step, index) => {
          const clickable = step.decision != null;
          const isOpen = expandedOrder === step.order;
          return (
            <span key={step.order} className="inline-flex items-center gap-1.5">
              <button
                type="button"
                disabled={!clickable}
                onClick={() => setExpandedOrder(isOpen ? null : step.order)}
                title={clickable ? t("apply.approvalDecisionToggle") : undefined}
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] transition-colors ${stateClass(step.status)} ${
                  clickable ? "cursor-pointer hover:opacity-90" : "cursor-default"
                } ${isOpen ? "ring-2 ring-secondary-400" : ""}`}
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className={stateIcon(step.status)} />
                </span>
                {step.name}
                {clickable && (
                  <span className="flex h-3 w-3 items-center justify-center">
                    <i className={isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
                  </span>
                )}
              </button>
              {index < progress.steps.length - 1 && (
                <span className="flex h-3 w-3 items-center justify-center text-foreground-400">
                  <i className="ri-arrow-right-s-line" />
                </span>
              )}
            </span>
          );
        })}
      </div>

      {progress.steps.some((step) => step.decision != null) && (
        <p className="mt-2 flex items-center gap-1.5 text-[11px] text-secondary-800">
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className="ri-cursor-line" />
          </span>
          {t("apply.approvalDecisionHint")}
        </p>
      )}

      {expanded && expanded.decision && (
        <div className="mt-2.5 rounded-md border border-secondary-200 bg-background-50 px-3 py-2.5">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`flex h-5 w-5 items-center justify-center rounded-full ${
                expanded.decision.decision === "approved"
                  ? "bg-primary-100 text-primary-700"
                  : "bg-accent-100 text-accent-800"
              }`}
            >
              <i className={expanded.decision.decision === "approved" ? "ri-check-line" : "ri-close-line"} />
            </span>
            <span className="text-xs font-medium text-foreground-950">{expanded.name}</span>
            <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] font-medium text-secondary-900">
              {expanded.decision.decision === "approved"
                ? t("approvals.statusApproved")
                : t("approvals.statusRejected")}
            </span>
          </div>
          <dl className="mt-2 flex flex-col gap-1.5 text-[11px]">
            <div className="flex items-start gap-1.5">
              <dt className="flex shrink-0 items-center gap-1 text-foreground-500">
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-user-line" />
                </span>
                {t("apply.approvalDecisionApprover")}
              </dt>
              <dd className="text-foreground-800">{expanded.decision.approverName ?? t("approvals.unknown")}</dd>
            </div>
            <div className="flex items-start gap-1.5">
              <dt className="flex shrink-0 items-center gap-1 text-foreground-500">
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-chat-3-line" />
                </span>
                {t("apply.approvalDecisionNote")}
              </dt>
              <dd className="text-foreground-800">{expanded.decision.note || t("apply.approvalDecisionNoNote")}</dd>
            </div>
            <div className="flex items-start gap-1.5">
              <dt className="flex shrink-0 items-center gap-1 text-foreground-500">
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-time-line" />
                </span>
                {t("apply.approvalDecisionTime")}
              </dt>
              <dd className="text-foreground-800">{formatFullDate(expanded.decision.createdAt)}</dd>
            </div>
          </dl>
        </div>
      )}

      {isPending && (
        <p className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-secondary-800">
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className="ri-map-pin-time-line" />
          </span>
          {t("apply.approvalCurrentHint", {
            name: progress.currentStepName ?? `#${progress.currentStep}`,
            current: progress.currentStep,
            total: progress.totalSteps,
          })}
          {progress.escalated && (
            <span className="inline-flex items-center gap-1 rounded-full bg-secondary-500 px-2 py-0.5 text-[10px] font-medium text-background-50">
              <i className="ri-arrow-up-double-line" />
              {t("approvals.escalatedTag")}
            </span>
          )}
        </p>
      )}
    </div>
  );
}